"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getChordIntervals = exports.parseChord = exports.getChord = exports.CHORD_INTERVALS = void 0;
const notes_1 = require("./notes");
const format_1 = require("./util/format");
exports.CHORD_INTERVALS = {
    "M": [0, 4, 7],
    "m": [0, 3, 7],
    "7": [0, 4, 7, 10],
    "M7": [0, 4, 7, 11],
    "m7": [0, 3, 7, 10],
    "dim": [0, 3, 6],
    "dim7": [0, 3, 6, 9],
    "sus4": [0, 5, 7],
    "sus2": [0, 2, 7],
    "aug": [0, 4, 8],
    "5": [0, 7],
    "M6": [0, 4, 7, 9],
    "m6": [0, 3, 7, 9],
    "add2": [0, 2, 4, 7],
    "9": [0, 4, 7, 10, 14],
    "M9": [0, 4, 7, 11, 14],
    "m9": [0, 3, 7, 10, 14],
};
class ChordImpl {
    get isInverted() {
        return !this.root.equalsIgnoreOctave(this.bass);
    }
    constructor(root, quality = "M", bass = root) {
        this.root = root;
        this.quality = quality || "M";
        this.bass = bass;
        if (this.isInverted && !this.hasNote(bass)) {
            throw new Error(`Bass note '${bass.name}' is not a member of this chord`);
        }
        this.name = root.name + (quality === "M" ? "" : quality) + (this.isInverted ? ("/" + bass.name) : "");
    }
    get aliasChord() {
        if (this.root.alias) {
            const bass = this.isInverted ? (this.bass.alias ? this.bass.alias : this.bass) : this.root.alias;
            return getChord(this.root.alias, this.quality, bass);
        }
        return this;
    }
    get formattedName() {
        return (0, format_1.formatAccidentals)(this.name);
    }
    get notes() {
        return this._notes || (this._notes = getChordNotes(this));
    }
    get accidental() {
        return this.root.accidental;
    }
    get hasAccidental() {
        return this.accidental.length > 0;
    }
    get isSharp() {
        return this.accidental === "#";
    }
    get isFlat() {
        return this.accidental === "b";
    }
    get inversionCount() {
        return this.notes.length;
    }
    get intervals() {
        return getChordIntervals(this.quality);
    }
    getInversion(inversion) {
        return getChord(this.root, this.quality, this.notes[inversion]);
    }
    transpose(steps) {
        return getChord(this.root.transpose(steps), this.quality, this.bass.transpose(steps));
    }
    toString() {
        return this.name;
    }
    equals(chord) {
        return this.name === chord.name;
    }
    equalsIgnoreBass(chord) {
        return this.root.number === chord.root.number
            && this.quality === chord.quality;
    }
    isSameAs(chord) {
        return this.root.number === chord.root.number
            && this.quality === chord.quality
            && this.bass.number === chord.bass.number;
    }
    hasNote(note) {
        return this.notes.findIndex(n => n.number === note.number) >= 0;
    }
}
function getChord(root, quality = "M", bass) {
    if (typeof root === "string") {
        root = (0, notes_1.getNote)(root);
    }
    if (typeof bass === "string") {
        bass = (0, notes_1.getNote)(bass);
    }
    return new ChordImpl(root, quality, bass);
}
exports.getChord = getChord;
/**
 * Parses a chord name into a Chord object, or undefined if not valid.
 * E.g. C#sus4/G#
 * @param chord Chord name which contains a root note and optionally a quality and bass note
 */
function parseChord(chord) {
    const parts = /([A-G][#,b]?)([a-zA-Z]*\d?)?(\/[A-G][#,b]?)?/.exec(chord);
    if (parts && parts[1]) {
        const root = parts[1];
        // Default quality is Major
        const quality = (parts[2] || "M");
        // Slice to remove the slash
        const bass = (parts[3] ? parts[3].slice(1) : root);
        return getChord(root, quality, bass);
    }
    return undefined;
}
exports.parseChord = parseChord;
/**
 * Gets the chord intervals for the specified chord quality
 */
function getChordIntervals(quality) {
    const ints = exports.CHORD_INTERVALS[quality];
    if (ints === undefined) {
        throw new Error("Unknown quality " + quality);
    }
    return ints;
}
exports.getChordIntervals = getChordIntervals;
/**
 * Gets the notes for the chord
 * @param name A chord
 * @param inversion Inversion of the chord
 * @return Set of notes that make up the chord
 */
function getChordNotes(chord) {
    const root = chord.root;
    const intervals = getChordIntervals(chord.quality);
    const notes = [root];
    for (let i = 1; i < intervals.length; i++) {
        let note = (0, notes_1.getNote)((root.number + intervals[i]) % 12);
        if (note.alias && note.accidental !== chord.accidental && note.alias.accidental === chord.accidental) {
            note = note.alias;
        }
        notes.push(note);
    }
    if (chord.bass !== chord.root) {
        // Adjust notes to the inversion specified by the bass note
        const inversion = notes.findIndex(n => n.equalsIgnoreOctave(chord.bass));
        invertNotes(notes, inversion);
    }
    return notes;
}
function invertNotes(notes, inversion) {
    // Shift the notes for the number of variation
    for (let i = 0; i < inversion; i++) {
        notes.push(notes.shift());
    }
    return notes;
}
//# sourceMappingURL=chords.js.map